import {
  TaxiCorporationDriverPlanEntry,
  TaxiCorporationDriverPlanFullPlanEntry,
  TaxiCorporationDriverPlanFullPlanEntryCheckoutEvent,
  TaxiCorporationDriverPlanFullPlanEntryEndNode,
  TaxiCorporationDriverPlanFullPlanEntrySolvedOrders,
  TaxiCorporationDriverPlanFullPlanEntryStartNode,
  TaxiCorporationDriverPlanRestrictedPlanEntry,
  TaxiCorporationDriverPlanRestrictedPlanEntryEndNode,
  TaxiCorporationDriverPlanRestrictedPlanEntryStartNode,
} from "../../../../../../../common/services/taxi-corporation/driver/plan/taxi-corporation-driver-plan";
import PlanEntryType from "../../../../../../../common/types/plan-entry-type";
import DriverDetailsDriverPlanEntry, {
  DriverDetailsDriverPlanEntryNodeExclusions,
  DriverDetailsDriverPlanEntryWaypoint,
  DriverDetailsDriverPlanEntryWaypointType,
} from "../../common/types/driver-details-plan-entry";

const checkIsRestrictedResponsePlanEntry = (
  responsePlanEntry: TaxiCorporationDriverPlanEntry,
): responsePlanEntry is TaxiCorporationDriverPlanRestrictedPlanEntry => {
  return responsePlanEntry.viewType === PlanEntryType.RESTRICTED;
};

const createWaypointForFullPlanEntryStartNode = (
  responseStartNode: TaxiCorporationDriverPlanFullPlanEntryStartNode,
  responseCheckoutEvents: TaxiCorporationDriverPlanFullPlanEntryCheckoutEvent[],
  planEntryType: PlanEntryType,
  stageCounter: number,
): DriverDetailsDriverPlanEntryWaypoint => {
  const responseCheckoutEvent = responseCheckoutEvents.find(
    (item) => item.nodeId === responseStartNode.meta.id,
  );

  const waypoint: DriverDetailsDriverPlanEntryWaypoint = {
    type: DriverDetailsDriverPlanEntryWaypointType.DRIVER_HOME,
    uuid: responseStartNode.meta.id,
    address: {
      displayName: responseStartNode.displayName,
      latitude: responseStartNode.lat,
      longitude: responseStartNode.lon,
    },
    checkoutDate: responseCheckoutEvent?.achievedAt
      ? new Date(responseCheckoutEvent.achievedAt)
      : null,
    clientDisplayName: "",
    estimatedDate: new Date(responseStartNode.meta.estimatedTime),
    plannedHaltingTime: responseStartNode.haltingTime,
    forcedHaltingTime: responseCheckoutEvent?.haltingTime ?? null,
    highwayCost: responseCheckoutEvent?.highwayCharge ?? null,
    createdAt: responseCheckoutEvent?.createdAt ?? null,
    createdByName: responseCheckoutEvent?.createdByName ?? null,
    order: null,
    plannedDate: responseStartNode.time
      ? new Date(responseStartNode.time)
      : null,
    isCheckouted: !!responseCheckoutEvent,
    checkoutEventUuid: responseCheckoutEvent?.id ?? null,
    isCheckoutManagementEnabled: planEntryType === PlanEntryType.FULL,
    solvedOrderUuid: null,
    stageNo: stageCounter,
  };

  return waypoint;
};

const createWaypointForFullPlanEntryEndNode = (
  responseEndNode: TaxiCorporationDriverPlanFullPlanEntryEndNode,
  responseCheckoutEvents: TaxiCorporationDriverPlanFullPlanEntryCheckoutEvent[],
  planEntryType: PlanEntryType,
  stageCounter: number,
): DriverDetailsDriverPlanEntryWaypoint => {
  const responseCheckoutEvent = responseCheckoutEvents.find(
    (item) => item.nodeId === responseEndNode.meta.id,
  );

  const waypoint: DriverDetailsDriverPlanEntryWaypoint = {
    type: DriverDetailsDriverPlanEntryWaypointType.DRIVER_HOME,
    uuid: responseEndNode.meta.id,
    address: {
      displayName: responseEndNode.displayName,
      latitude: responseEndNode.lat,
      longitude: responseEndNode.lon,
    },
    checkoutDate: responseCheckoutEvent?.achievedAt
      ? new Date(responseCheckoutEvent.achievedAt)
      : null,
    clientDisplayName: "",
    estimatedDate: new Date(responseEndNode.meta.estimatedTime),
    plannedHaltingTime: responseEndNode.haltingTime,
    forcedHaltingTime: responseCheckoutEvent?.haltingTime ?? null,
    highwayCost: responseCheckoutEvent?.highwayCharge ?? null,
    createdAt: responseCheckoutEvent?.createdAt ?? null,
    createdByName: responseCheckoutEvent?.createdByName ?? null,
    order: null,
    plannedDate: responseEndNode.time ? new Date(responseEndNode.time) : null,
    isCheckouted: !!responseCheckoutEvent,
    checkoutEventUuid: responseCheckoutEvent?.id ?? null,
    isCheckoutManagementEnabled: planEntryType === PlanEntryType.FULL,
    solvedOrderUuid: null,
    stageNo: stageCounter,
  };

  return waypoint;
};

const createWaypointForRestrictedPlanEntryStartNode = (
  responseStartNode: TaxiCorporationDriverPlanRestrictedPlanEntryStartNode,
  responseCheckoutEvents: TaxiCorporationDriverPlanFullPlanEntryCheckoutEvent[],
  planEntryType: PlanEntryType,
  stageCounter: number,
): DriverDetailsDriverPlanEntryWaypoint => {
  const responseCheckoutEvent = responseCheckoutEvents.find(
    (item) => item.nodeId === responseStartNode.meta.id,
  );

  const waypoint: DriverDetailsDriverPlanEntryWaypoint = {
    type: DriverDetailsDriverPlanEntryWaypointType.DRIVER_HOME,
    uuid: responseStartNode.meta.id,
    address: {
      displayName: responseStartNode.displayName,
      latitude: responseStartNode.lat,
      longitude: responseStartNode.lon,
    },
    checkoutDate: responseCheckoutEvent?.achievedAt
      ? new Date(responseCheckoutEvent.achievedAt)
      : null,
    clientDisplayName: "",
    estimatedDate: new Date(responseStartNode.meta.estimatedTime),
    plannedHaltingTime: responseStartNode.haltingTime,
    forcedHaltingTime: responseCheckoutEvent?.haltingTime ?? null,
    highwayCost: responseCheckoutEvent?.highwayCharge ?? null,
    createdAt: responseCheckoutEvent?.createdAt ?? null,
    createdByName: responseCheckoutEvent?.createdByName ?? null,
    order: null,
    plannedDate: responseStartNode.time
      ? new Date(responseStartNode.time)
      : null,
    isCheckouted: !!responseCheckoutEvent,
    checkoutEventUuid: responseCheckoutEvent?.id ?? null,
    isCheckoutManagementEnabled: planEntryType === PlanEntryType.FULL,
    solvedOrderUuid: null,
    stageNo: stageCounter,
  };

  return waypoint;
};

const createWaypointForRestrictedPlanEntryEndNode = (
  responseEndNode: TaxiCorporationDriverPlanRestrictedPlanEntryEndNode,
  responseCheckoutEvents: TaxiCorporationDriverPlanFullPlanEntryCheckoutEvent[],
  planEntryType: PlanEntryType,
  stageCounter: number,
): DriverDetailsDriverPlanEntryWaypoint => {
  const responseCheckoutEvent = responseCheckoutEvents.find(
    (item) => item.nodeId === responseEndNode.meta.id,
  );

  const waypoint: DriverDetailsDriverPlanEntryWaypoint = {
    type: DriverDetailsDriverPlanEntryWaypointType.DRIVER_HOME,
    uuid: responseEndNode.meta.id,
    address: {
      displayName: responseEndNode.displayName,
      latitude: responseEndNode.lat,
      longitude: responseEndNode.lon,
    },
    checkoutDate: responseCheckoutEvent?.achievedAt
      ? new Date(responseCheckoutEvent.achievedAt)
      : null,
    clientDisplayName: "",
    estimatedDate: new Date(responseEndNode.meta.estimatedTime),
    plannedHaltingTime: responseEndNode.haltingTime,
    forcedHaltingTime: responseCheckoutEvent?.haltingTime ?? null,
    highwayCost: responseCheckoutEvent?.highwayCharge ?? null,
    createdAt: responseCheckoutEvent?.createdAt ?? null,
    createdByName: responseCheckoutEvent?.createdByName ?? null,
    order: null,
    plannedDate: responseEndNode.time ? new Date(responseEndNode.time) : null,
    isCheckouted: !!responseCheckoutEvent,
    checkoutEventUuid: responseCheckoutEvent?.id ?? null,
    isCheckoutManagementEnabled: planEntryType === PlanEntryType.FULL,
    solvedOrderUuid: null,
    stageNo: stageCounter,
  };

  return waypoint;
};

const createWaypointsForSolvedOrder = (
  responseSolvedOrder: TaxiCorporationDriverPlanFullPlanEntrySolvedOrders,
  responseCheckoutEvents: TaxiCorporationDriverPlanFullPlanEntryCheckoutEvent[],
  stageCounter: number,
  onComplete: (stageCounterValue: number) => void,
): DriverDetailsDriverPlanEntryWaypoint[] => {
  const waypoints: DriverDetailsDriverPlanEntryWaypoint[] = [];

  let internalStageCounter = stageCounter;

  responseSolvedOrder.ride.seq.forEach((seqItem) => {
    const responseCheckoutEvent = responseCheckoutEvents.find(
      (item) => item.nodeId === seqItem.meta.id,
    );

    const waypoint: DriverDetailsDriverPlanEntryWaypoint = {
      type: DriverDetailsDriverPlanEntryWaypointType.ORDER_WAYPOINT,
      uuid: seqItem.meta.id,
      clientDisplayName: "",
      order: {
        uuid: responseSolvedOrder.transportingOrder.cargoOrder.id,
        cargoInternalOrderId:
          responseSolvedOrder.transportingOrder.cargoOrder.humanId,
      },
      address: {
        displayName: seqItem.displayName,
        latitude: seqItem.lat,
        longitude: seqItem.lon,
      },
      checkoutDate: responseCheckoutEvent?.achievedAt
        ? new Date(responseCheckoutEvent.achievedAt)
        : null,
      createdAt: responseCheckoutEvent?.createdAt ?? null,
      createdByName: responseCheckoutEvent?.createdByName ?? null,
      estimatedDate: new Date(seqItem.meta.estimatedTime),
      plannedHaltingTime: seqItem.haltingTime,
      forcedHaltingTime: responseCheckoutEvent?.haltingTime ?? null,
      highwayCost: responseCheckoutEvent?.highwayCharge ?? null,
      plannedDate: seqItem.time ? new Date(seqItem.time) : null,
      isCheckouted: !!responseCheckoutEvent,
      checkoutEventUuid: responseCheckoutEvent?.id ?? null,
      isCheckoutManagementEnabled: true,
      solvedOrderUuid: responseSolvedOrder.id,
      stageNo: internalStageCounter,
    };

    waypoints.push(waypoint);
    internalStageCounter++;
  });

  onComplete(internalStageCounter);

  return waypoints;
};

const createWaypointsForSolvedOrders = (
  responseSolvedOrders: TaxiCorporationDriverPlanFullPlanEntrySolvedOrders[],
  responseCheckoutEvents: TaxiCorporationDriverPlanFullPlanEntryCheckoutEvent[],
  initialStageCounter: number,
): DriverDetailsDriverPlanEntryWaypoint[] => {
  let stageCounter = initialStageCounter;
  const allWaypoints: DriverDetailsDriverPlanEntryWaypoint[] = [];

  responseSolvedOrders.forEach((responseSolvedOrder) => {
    const orderWaypoints = createWaypointsForSolvedOrder(
      responseSolvedOrder,
      responseCheckoutEvents,
      stageCounter,
      (updatedStageCounter) => {
        stageCounter = updatedStageCounter;
      },
    );

    allWaypoints.push(...orderWaypoints);
  });

  return allWaypoints;
};

const createPlanEntryWaypointsForRestrictedPlanEntry = (
  responseRestrictedPlanEntry: TaxiCorporationDriverPlanRestrictedPlanEntry,
): DriverDetailsDriverPlanEntryWaypoint[] => {
  const waypoints: DriverDetailsDriverPlanEntryWaypoint[] = [];

  let stageCounter: number = 1;

  const startWaypoint = createWaypointForRestrictedPlanEntryStartNode(
    responseRestrictedPlanEntry.startNode,
    [],
    responseRestrictedPlanEntry.viewType,
    stageCounter,
  );

  waypoints.push(startWaypoint);

  const endWaypoint = createWaypointForRestrictedPlanEntryEndNode(
    responseRestrictedPlanEntry.endNode,
    [],
    responseRestrictedPlanEntry.viewType,
    stageCounter,
  );

  waypoints.push(endWaypoint);

  return waypoints;
};

const createPlanEntryWaypointsForFullPlanEntry = (
  responseFullPlanEntry: TaxiCorporationDriverPlanFullPlanEntry,
): DriverDetailsDriverPlanEntryWaypoint[] => {
  const waypoints: DriverDetailsDriverPlanEntryWaypoint[] = [];

  let stageCounter: number = 1;

  const startWaypoint = createWaypointForFullPlanEntryStartNode(
    responseFullPlanEntry.startNode,
    responseFullPlanEntry.checkoutEvents,
    responseFullPlanEntry.viewType,
    stageCounter,
  );

  stageCounter++;

  waypoints.push(startWaypoint);

  const orderWaypoints = createWaypointsForSolvedOrders(
    responseFullPlanEntry.solvedOrders,
    responseFullPlanEntry.checkoutEvents,
    stageCounter,
  );

  waypoints.push(...orderWaypoints);

  stageCounter = stageCounter = waypoints[waypoints.length - 1].stageNo + 1;

  const endWaypoint = createWaypointForFullPlanEntryEndNode(
    responseFullPlanEntry.endNode,
    responseFullPlanEntry.checkoutEvents,
    responseFullPlanEntry.viewType,
    stageCounter,
  );

  waypoints.push(endWaypoint);

  return waypoints;
};

const createPlanEntryWaypoints = (
  responsePlanEntry: TaxiCorporationDriverPlanEntry,
): DriverDetailsDriverPlanEntryWaypoint[] => {
  const isResponsePlanEntryRestricted =
    checkIsRestrictedResponsePlanEntry(responsePlanEntry);

  if (isResponsePlanEntryRestricted) {
    return createPlanEntryWaypointsForRestrictedPlanEntry(responsePlanEntry);
  }

  return createPlanEntryWaypointsForFullPlanEntry(responsePlanEntry);
};

const createNodeExclusions = (
  responsePlanEntry: TaxiCorporationDriverPlanEntry,
): DriverDetailsDriverPlanEntryNodeExclusions[] => {
  const exclusionList: DriverDetailsDriverPlanEntryNodeExclusions[] = [];

  if (checkIsRestrictedResponsePlanEntry(responsePlanEntry)) {
    return exclusionList;
  }

  responsePlanEntry.nodeExclusions.exclusions.forEach((nodeExclusions) => {
    exclusionList.push({
      approaching: nodeExclusions.excludeApproaching,
      inside: nodeExclusions.excludeInside,
      returning: nodeExclusions.excludeReturning,
      nodeIds: nodeExclusions.nodeIds,
    });
  });

  return exclusionList;
};

const createPlanEntry = (
  responsePlanEntry: TaxiCorporationDriverPlanEntry,
): DriverDetailsDriverPlanEntry => {
  const waypoints = createPlanEntryWaypoints(responsePlanEntry);
  const nodeExclusions = createNodeExclusions(responsePlanEntry);

  return {
    isHidden: false,
    isAnonymized: responsePlanEntry.viewType === PlanEntryType.RESTRICTED,
    uuid: responsePlanEntry.id,
    id: responsePlanEntry.humanId,
    waypoints,
    nodeExclusions: nodeExclusions,
  };
};

const createPlanEntries = (
  responsePlanEntries: TaxiCorporationDriverPlanEntry[],
): DriverDetailsDriverPlanEntry[] => {
  return responsePlanEntries.map(createPlanEntry);
};

const driverDetailsPlanByTaxiPlanEntryFactory = {
  createPlanEntries,
};

export default driverDetailsPlanByTaxiPlanEntryFactory;
